import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import LocationPicker from "@/components/LocationPicker";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import NumberField from "@/components/NumberField";
import { fmtNum } from "@/utils/toEnglishDigits";
import {
  setDepartureRules,
  loadDepartureRules,
} from "@/utils/departureRulesCache";
import {
  Plus,
  MagnifyingGlass,
  PencilSimple,
  TrashSimple,
  X,
  Path,
  ArrowLeft,
  Spinner,
  Clock,
  Info,
  DownloadSimple,
  CheckCircle,
} from "@phosphor-icons/react";

// Arabic-friendly offset formatter — e.g. 180 → "3 ساعات", 90 → "ساعة ونصف".
function formatOffset(min) {
  const h = Math.floor(min / 60);
  const m = min % 60;
  const HRS_AR = {
    1: "ساعة",
    2: "ساعتين",
    3: "3 ساعات",
    4: "4 ساعات",
    5: "5 ساعات",
    6: "6 ساعات",
    7: "7 ساعات",
    8: "8 ساعات",
    9: "9 ساعات",
    10: "10 ساعات",
  };
  const hLabel = h > 0 ? (HRS_AR[h] || `${h} ساعة`) : "";
  if (m === 0) return hLabel || `${min} دقيقة`;
  if (m === 30) return h === 0 ? "نصف ساعة" : `${hLabel} ونصف`;
  return h === 0 ? `${m} دقيقة` : `${hLabel} و ${m} دقيقة`;
}

const emptyForm = {
  pickup_location: "",
  destination: "",
  hours: "",
  minutes: "",
  note: "",
  is_active: true,
};

export default function DepartureRules() {
  const navigate = useNavigate();
  const confirm = useConfirm();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  // ── Bulk-import state ──────────────────────────────────────────────
  const [importOpen, setImportOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importList, setImportList] = useState([]);       // [{pickup, destination, count}]
  const [importSelected, setImportSelected] = useState({});
  const [importHours, setImportHours] = useState({});
  const [importMinutes, setImportMinutes] = useState({});
  const [importSaving, setImportSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/departure-rules");
      setRules(data);
      // Keep the shared cache in sync so bookings + WhatsApp use the new list.
      setDepartureRules((data || []).filter((r) => r.is_active !== false));
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rules;
    return rules.filter(
      (r) =>
        (r.pickup_location || "").toLowerCase().includes(q) ||
        (r.destination || "").toLowerCase().includes(q) ||
        (r.note || "").toLowerCase().includes(q),
    );
  }, [rules, search]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (r) => {
    setEditingId(r.id);
    setForm({
      pickup_location: r.pickup_location || "",
      destination: r.destination || "",
      hours: String(Math.floor((r.offset_minutes || 0) / 60) || ""),
      minutes: String((r.offset_minutes || 0) % 60 || ""),
      note: r.note || "",
      is_active: r.is_active !== false,
    });
    setModalOpen(true);
  };

  const totalMinutes = useMemo(() => {
    const h = parseInt(form.hours || "0", 10) || 0;
    const m = parseInt(form.minutes || "0", 10) || 0;
    return h * 60 + m;
  }, [form.hours, form.minutes]);

  const save = async () => {
    if (!form.pickup_location.trim() || !form.destination.trim()) {
      return toast.error("من (pickup) وإلى (destination) مطلوبان");
    }
    if (totalMinutes <= 0) return toast.error("أدخل ساعات و/أو دقائق (أكبر من صفر)");
    if (totalMinutes > 1440) return toast.error("لا يمكن تجاوز 24 ساعة");

    setSaving(true);
    try {
      const body = {
        pickup_location: form.pickup_location.trim(),
        destination: form.destination.trim(),
        offset_minutes: totalMinutes,
        note: form.note.trim() || null,
        is_active: !!form.is_active,
      };
      if (editingId) {
        await api.patch(`/admin/departure-rules/${editingId}`, body);
        toast.success("تم حفظ التعديلات");
      } else {
        await api.post("/admin/departure-rules", body);
        toast.success("تم إنشاء القاعدة");
      }
      setModalOpen(false);
      await load();
      // Force-refresh the global cache so consumers see the change immediately.
      await loadDepartureRules(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (r) => {
    const ok = await confirm({
      title: "حذف قاعدة",
      message: `سيتم حذف قاعدة ${r.pickup_location} → ${r.destination} نهائياً.`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/departure-rules/${r.id}`);
      toast.success("تم الحذف");
      await load();
      await loadDepartureRules(true);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ── Import from existing route-pricing rules ──────────────────────
  const openImport = async () => {
    setImportOpen(true);
    setImportLoading(true);
    setImportSelected({});
    setImportHours({});
    setImportMinutes({});
    try {
      const { data } = await api.get("/route-rules");
      const routeRules = Array.isArray(data) ? data : [];
      // Unique (pickup, destination) pairs with usage count.
      const map = new Map();
      for (const rr of routeRules) {
        const pu = String(rr.pickup_pattern || "").trim();
        const de = String(rr.destination_pattern || "").trim();
        if (!pu || !de) continue;
        const key = `${pu}|||${de}`;
        const existing = map.get(key);
        if (existing) existing.count += 1;
        else map.set(key, { pickup: pu, destination: de, count: 1 });
      }
      // Exclude pairs that already exist in departure rules.
      const existingKeys = new Set(
        rules.map((r) => `${r.pickup_location}|||${r.destination}`),
      );
      const arr = Array.from(map.values())
        .filter((p) => !existingKeys.has(`${p.pickup}|||${p.destination}`))
        .sort((a, b) => b.count - a.count);
      setImportList(arr);
    } catch (err) {
      toast.error(formatApiError(err));
      setImportOpen(false);
    } finally {
      setImportLoading(false);
    }
  };

  const toggleImport = (key) => {
    setImportSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const selectAllImport = () => {
    const next = {};
    for (const item of importList) next[`${item.pickup}|||${item.destination}`] = true;
    setImportSelected(next);
  };

  const clearAllImport = () => setImportSelected({});

  const bulkImport = async () => {
    const selectedKeys = Object.keys(importSelected).filter((k) => importSelected[k]);
    if (selectedKeys.length === 0) return toast.error("اختر مساراً واحداً على الأقل");
    for (const key of selectedKeys) {
      const h = parseInt(importHours[key] || "0", 10) || 0;
      const m = parseInt(importMinutes[key] || "0", 10) || 0;
      if (h * 60 + m <= 0) return toast.error("أدخل ساعات أو دقائق لكل مسار محدد");
    }
    setImportSaving(true);
    let ok = 0, skipped = 0, failed = 0;
    for (const key of selectedKeys) {
      const item = importList.find((x) => `${x.pickup}|||${x.destination}` === key);
      if (!item) continue;
      const h = parseInt(importHours[key] || "0", 10) || 0;
      const m = parseInt(importMinutes[key] || "0", 10) || 0;
      try {
        await api.post("/admin/departure-rules", {
          pickup_location: item.pickup,
          destination: item.destination,
          offset_minutes: h * 60 + m,
          is_active: true,
        });
        ok++;
      } catch (err) {
        if (err?.response?.status === 409) skipped++;
        else failed++;
      }
    }
    setImportSaving(false);
    setImportOpen(false);
    await load();
    await loadDepartureRules(true);
    const parts = [`أُنشئت ${ok} قاعدة`];
    if (skipped) parts.push(`${skipped} موجودة مسبقاً`);
    if (failed) parts.push(`${failed} فشلت`);
    toast.success(parts.join(" · "));
  };
  const selectedCount = Object.values(importSelected).filter(Boolean).length;

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-4xl mx-auto px-4 py-6">
        {/* Header */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="w-9 h-9 rounded-full border border-primary/25 text-primary hover:bg-primary/10 flex items-center justify-center"
              data-testid="dep-back"
            >
              <ArrowLeft size={16} />
            </button>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-2">
                <Clock size={24} weight="duotone" className="text-primary" />
                قواعد وقت التحرك
              </h1>
              <p className="text-white/50 text-sm mt-1">
                <span className="num">{fmtNum(rules.length)}</span> قاعدة
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={openImport}
              data-testid="dep-import"
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl font-bold border border-primary/40 bg-primary/10 text-primary hover:bg-primary/20"
            >
              <DownloadSimple size={16} weight="bold" /> استيراد المسارات
            </button>
            <button
              onClick={openAdd}
              data-testid="dep-add"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold"
            >
              <Plus size={18} weight="bold" /> قاعدة جديدة
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-4">
          <MagnifyingGlass
            size={16}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-white/40"
          />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="ابحث بالمدينة أو الوجهة أو الملاحظة…"
            className="mz-input pr-10"
            data-testid="dep-search"
          />
        </div>

        {/* Info banner */}
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-3 mb-5 flex items-start gap-2 text-[13px] text-white/80 leading-relaxed">
          <Info size={16} weight="fill" className="text-sky-400 mt-0.5 flex-shrink-0" />
          <span>
            القواعد تحدّد <b>وقت تحرّك السائق قبل موعد الحجز</b>. تُستخدم تلقائياً في رسائل الواتساب
            وعمود "التحرك" في الجدول. المطابقة ذكية (قاعدة "مكة" تنطبق على "مكة الفيرمونت").
          </span>
        </div>

        {/* List */}
        {loading ? (
          <div className="flex items-center justify-center py-20 text-primary">
            <Spinner size={28} className="animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="text-center py-16 bg-card border border-primary/15 rounded-2xl space-y-3"
            data-testid="dep-empty"
          >
            <Clock size={36} weight="duotone" className="mx-auto text-primary/50" />
            <div className="text-white/60 text-sm">
              {search ? "لا توجد نتائج مطابقة" : "لا توجد قواعد بعد"}
            </div>
            {!search && (
              <button
                onClick={openAdd}
                data-testid="dep-add-first"
                className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-xs font-bold bg-primary/15 border border-primary/40 text-primary hover:bg-primary/25"
              >
                <Plus size={12} weight="bold" /> إضافة أوّل قاعدة
              </button>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {filtered.map((r) => (
              <RuleCard
                key={r.id}
                rule={r}
                onEdit={() => openEdit(r)}
                onDelete={() => remove(r)}
              />
            ))}
          </div>
        )}
      </main>

      {modalOpen && (
        <RuleFormModal
          editing={!!editingId}
          form={form}
          setForm={setForm}
          totalMinutes={totalMinutes}
          saving={saving}
          onSave={save}
          onCancel={() => setModalOpen(false)}
        />
      )}

      {importOpen && (
        <ImportModal
          loading={importLoading}
          list={importList}
          selected={importSelected}
          hours={importHours}
          minutes={importMinutes}
          selectedCount={selectedCount}
          saving={importSaving}
          onToggle={toggleImport}
          onSelectAll={selectAllImport}
          onClearAll={clearAllImport}
          onHoursChange={(k, v) =>
            setImportHours((p) => ({ ...p, [k]: String(v || "").replace(/\D/g, "").slice(0, 2) }))
          }
          onMinutesChange={(k, v) =>
            setImportMinutes((p) => ({ ...p, [k]: String(v || "").replace(/\D/g, "").slice(0, 2) }))
          }
          onSave={bulkImport}
          onCancel={() => setImportOpen(false)}
        />
      )}
    </div>
  );
}

function RuleCard({ rule, onEdit, onDelete }) {
  const disabled = rule.is_active === false;
  return (
    <div
      data-testid={`dep-card-${rule.id}`}
      className={`fadeup bg-card border rounded-2xl p-4 flex flex-col gap-2 transition-all hover:border-primary/50 ${
        disabled ? "opacity-60 border-white/10" : "border-primary/20"
      }`}
    >
      <div className="flex items-center gap-2 text-white font-bold">
        <Path size={18} weight="duotone" className="text-primary" />
        <span>{rule.pickup_location}</span>
        <ArrowLeft size={12} className="text-primary/60" />
        <span>{rule.destination}</span>
        {disabled && (
          <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/10 border border-white/20 text-white/60 mr-auto">
            موقوفة
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 text-primary text-sm font-bold">
        <Clock size={14} weight="fill" />
        <span>قبل الحجز بـ {formatOffset(rule.offset_minutes)}</span>
        <span className="text-white/40 text-[11px] num" dir="ltr">
          ({fmtNum(rule.offset_minutes)} دقيقة)
        </span>
      </div>
      {rule.note && (
        <div className="text-white/50 text-xs bg-primary/5 border border-primary/15 rounded-lg px-2 py-1.5 leading-relaxed">
          📝 {rule.note}
        </div>
      )}
      <div className="flex items-center gap-2 pt-2 border-t border-primary/10 mt-1">
        <button
          onClick={onEdit}
          data-testid={`dep-edit-${rule.id}`}
          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-xs font-semibold"
        >
          <PencilSimple size={13} weight="bold" /> تعديل
        </button>
        <button
          onClick={onDelete}
          data-testid={`dep-delete-${rule.id}`}
          className="p-1.5 rounded-lg border border-destructive/40 text-destructive hover:bg-destructive/10"
        >
          <TrashSimple size={13} weight="bold" />
        </button>
      </div>
    </div>
  );
}

const COMMON_PLACES = [
  "مكة",
  "المدينة",
  "جدة",
  "الطائف",
  "الرياض",
  "مطار جدة",
  "مطار المدينة",
  "مطار الطائف",
  "مطار الرياض",
  "قطار مكة",
  "قطار المدينة",
];

function RuleFormModal({ editing, form, setForm, totalMinutes, saving, onSave, onCancel }) {
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      dir="rtl"
      data-testid="dep-modal"
    >
      <div
        className="bg-card border border-primary/30 rounded-2xl w-full max-w-lg gold-glow flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-primary/15 flex items-center justify-between shrink-0">
          <h3 className="text-primary font-bold text-lg inline-flex items-center gap-2">
            <Clock size={18} weight="duotone" />
            {editing ? "تعديل قاعدة" : "قاعدة جديدة"}
          </h3>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-full text-white/50 hover:bg-white/10"
            data-testid="dep-modal-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-4">
          {/* Pickup */}
          <div>
            <label className="block text-xs text-white/60 mb-1 text-right">من (Pickup) *</label>
            <LocationPicker
              value={form.pickup_location}
              onChange={(v) => set("pickup_location", v)}
              placeholder="مثال: مكة"
              otherEndpointValue={form.destination}
              testId="dep-pickup"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {COMMON_PLACES.map((p) => (
                <button
                  key={`pu-${p}`}
                  type="button"
                  onClick={() => set("pickup_location", p)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-bold transition-colors ${
                    form.pickup_location === p
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-primary/5 border-primary/25 text-white/70 hover:bg-primary/15 hover:text-white"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Destination */}
          <div>
            <label className="block text-xs text-white/60 mb-1 text-right">إلى (Destination) *</label>
            <LocationPicker
              value={form.destination}
              onChange={(v) => set("destination", v)}
              placeholder="مثال: مطار جدة"
              otherEndpointValue={form.pickup_location}
              testId="dep-destination"
            />
            <div className="flex flex-wrap gap-1.5 mt-2">
              {COMMON_PLACES.map((p) => (
                <button
                  key={`dn-${p}`}
                  type="button"
                  onClick={() => set("destination", p)}
                  className={`text-[11px] px-2.5 py-1 rounded-full border font-bold transition-colors ${
                    form.destination === p
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-primary/5 border-primary/25 text-white/70 hover:bg-primary/15 hover:text-white"
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>

          {/* Offset */}
          <div>
            <label className="block text-xs text-white/60 mb-1 text-right">قبل موعد الحجز بـ *</label>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <NumberField
                  value={form.hours}
                  onChange={(v) => set("hours", v)}
                  min={0}
                  max={24}
                  className="mz-input num text-center"
                  placeholder="0"
                  testId="dep-hours"
                />
                <div className="text-[10px] text-white/40 text-center mt-1">ساعة</div>
              </div>
              <div>
                <NumberField
                  value={form.minutes}
                  onChange={(v) => set("minutes", v)}
                  min={0}
                  max={59}
                  className="mz-input num text-center"
                  placeholder="0"
                  testId="dep-minutes"
                />
                <div className="text-[10px] text-white/40 text-center mt-1">دقيقة</div>
              </div>
            </div>
            {totalMinutes > 0 && (
              <div
                className="mt-2 text-primary text-sm font-bold text-right"
                data-testid="dep-preview"
              >
                ⏰ الإجمالي: {formatOffset(totalMinutes)}
                <span className="text-white/40 text-[11px] num mr-1" dir="ltr">
                  ({fmtNum(totalMinutes)} دقيقة)
                </span>
              </div>
            )}
          </div>

          {/* Note */}
          <div>
            <label className="block text-xs text-white/60 mb-1 text-right">ملاحظة (اختياري)</label>
            <textarea
              value={form.note}
              onChange={(e) => set("note", e.target.value)}
              className="mz-input min-h-16"
              placeholder="مثال: زحمة الجمعة"
              data-testid="dep-note"
            />
          </div>

          {/* Active */}
          <label className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 cursor-pointer">
            <span className="text-sm text-white">مفعّلة</span>
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="w-5 h-5 accent-primary"
              data-testid="dep-active"
            />
          </label>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-primary/15 flex gap-3 shrink-0">
          <button
            type="button"
            onClick={onSave}
            disabled={saving}
            data-testid="dep-save"
            className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60"
          >
            {saving ? "جارٍ الحفظ…" : editing ? "حفظ التعديلات" : "إنشاء القاعدة"}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportModal({
  loading,
  list,
  selected,
  hours,
  minutes,
  selectedCount,
  saving,
  onToggle,
  onSelectAll,
  onClearAll,
  onHoursChange,
  onMinutesChange,
  onSave,
  onCancel,
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onCancel}
      dir="rtl"
      data-testid="dep-import-modal"
    >
      <div
        className="bg-card border border-primary/30 rounded-2xl w-full max-w-2xl gold-glow flex flex-col max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-primary/15 flex items-center justify-between shrink-0">
          <div>
            <h3 className="text-primary font-bold text-lg inline-flex items-center gap-2">
              <DownloadSimple size={18} weight="duotone" />
              استيراد المسارات المسجّلة
            </h3>
            <p className="text-white/50 text-xs mt-1">
              اختر مسارات من الأسعار وأدخل وقت التحرك لكل واحد.
            </p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="p-1.5 rounded-full text-white/50 hover:bg-white/10"
            data-testid="dep-import-close"
          >
            <X size={16} />
          </button>
        </div>

        {/* Toolbar */}
        {!loading && list.length > 0 && (
          <div className="px-5 pt-3 flex items-center justify-between gap-2 flex-wrap shrink-0">
            <div className="text-primary text-xs font-bold">
              📍 <span className="num">{list.length}</span> مسار متاح
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={onSelectAll}
                data-testid="dep-import-select-all"
                className="text-[11px] px-2.5 py-1 rounded-full border border-primary/40 text-primary hover:bg-primary/10 font-bold"
              >
                تحديد الكل
              </button>
              <button
                onClick={onClearAll}
                data-testid="dep-import-clear-all"
                className="text-[11px] px-2.5 py-1 rounded-full border border-white/20 text-white/60 hover:bg-white/5 font-bold"
              >
                مسح التحديد
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <div className="flex-1 overflow-y-auto min-h-0 p-5 space-y-2" data-testid="dep-import-list">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-primary">
              <Spinner size={28} className="animate-spin" />
            </div>
          ) : list.length === 0 ? (
            <div className="text-center py-12 space-y-3">
              <CheckCircle size={36} weight="duotone" className="mx-auto text-green-400" />
              <div className="text-white/70 text-sm">
                كل مساراتك المسعّرة مسجّلة بالفعل في قواعد وقت التحرك.
              </div>
            </div>
          ) : (
            list.map((item) => {
              const key = `${item.pickup}|||${item.destination}`;
              const active = !!selected[key];
              return (
                <div
                  key={key}
                  data-testid={`dep-import-row-${key}`}
                  className={`rounded-xl border p-3 transition-all ${
                    active
                      ? "border-primary bg-primary/10"
                      : "border-primary/15 bg-primary/5 hover:border-primary/40"
                  }`}
                >
                  <label className="flex items-start gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={active}
                      onChange={() => onToggle(key)}
                      className="w-5 h-5 accent-primary mt-0.5 flex-shrink-0"
                      data-testid={`dep-import-check-${key}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 text-white font-bold text-sm flex-wrap">
                        <Path size={14} weight="duotone" className="text-primary" />
                        <span className="truncate">{item.pickup}</span>
                        <ArrowLeft size={11} className="text-primary/60" />
                        <span className="truncate">{item.destination}</span>
                      </div>
                      <div className="text-white/40 text-[11px] mt-1">
                        مستخدم في <span className="num">{fmtNum(item.count)}</span> قاعدة أسعار
                      </div>
                    </div>
                  </label>

                  {active && (
                    <div className="mt-3 grid grid-cols-2 gap-2 pt-3 border-t border-primary/15">
                      <div>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={hours[key] || ""}
                          onChange={(e) => onHoursChange(key, e.target.value)}
                          placeholder="0"
                          maxLength={2}
                          className="mz-input num text-center"
                          style={{ direction: "ltr" }}
                          data-testid={`dep-import-h-${key}`}
                        />
                        <div className="text-[10px] text-white/40 text-center mt-1">ساعة</div>
                      </div>
                      <div>
                        <input
                          type="text"
                          inputMode="numeric"
                          value={minutes[key] || ""}
                          onChange={(e) => onMinutesChange(key, e.target.value)}
                          placeholder="0"
                          maxLength={2}
                          className="mz-input num text-center"
                          style={{ direction: "ltr" }}
                          data-testid={`dep-import-m-${key}`}
                        />
                        <div className="text-[10px] text-white/40 text-center mt-1">دقيقة</div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-primary/15 flex gap-3 shrink-0">
          <button
            type="button"
            onClick={onSave}
            disabled={saving || loading || selectedCount === 0}
            data-testid="dep-import-save"
            className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
          >
            {saving ? (
              "جارٍ الإضافة…"
            ) : (
              <>
                <Plus size={16} weight="bold" />
                إضافة (<span className="num">{fmtNum(selectedCount)}</span>)
              </>
            )}
          </button>
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-3 rounded-xl border border-white/20 text-white/70 hover:bg-white/5"
          >
            إلغاء
          </button>
        </div>
      </div>
    </div>
  );
}

